import React, { useState, useEffect } from 'react';
import { 
    faChartLine, 
    faUsers, 
    faClock, 
    faPlane, 
    faChevronLeft, 
    faChevronRight,
    faCalendarAlt,
    faExclamationTriangle,
    faUserClock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import api from '../../api/api';
import './admin-dashboard.scss';

export function AdminDashboard() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [monthlyStats, setMonthlyStats] = useState(null);
    const [inactiveCurrent, setInactiveCurrent] = useState([]);
    const [longInactive, setLongInactive] = useState([]);
    const [error, setError] = useState(null);

    const formatMonth = (date) => {
        return date.toLocaleDateString('es-ES', { 
            month: 'long', 
            year: 'numeric' 
        });
    };

    const fetchStats = async (date = currentDate) => {
        setLoading(true);
        setError(null);
        
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            
            const [monthlyResponse, inactiveResponse, longInactiveResponse] = await Promise.all([
                api.get(`/admin-stats/monthly-stats/${year}/${month}`),
                api.get(`/admin-stats/inactive-current-month/${year}/${month}`),
                api.get(`/admin-stats/long-inactive`)
            ]);
            
            setMonthlyStats(monthlyResponse.data);
            setInactiveCurrent(inactiveResponse.data);
            setLongInactive(longInactiveResponse.data);
            
        } catch (error) {
            console.error('Failed to fetch admin stats:', error);
            setError('Error loading statistics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
        fetchStats(newDate);
    };

    const formatHours = (hours) => {
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h}h ${m}m`;
    };

    const formatDaysAgo = (days) => {
        if (days === null) return t('never');
        if (days === 0) return t('today');
        if (days === 1) return t('yesterday');
        if (days < 30) return t('days-ago', { count: days });
        if (days < 365) return t('months-ago', { count: Math.floor(days / 30) });
        return t('years-ago', { count: Math.floor(days / 365) });
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading-container">
                    <FontAwesomeIcon icon={faChartLine} spin size="2x" />
                    <p>{t('loading-statistics')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-dashboard">
                <div className="error-container">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                    <p>{error}</p>
                    <button className="btn" onClick={() => fetchStats()}>
                        {t('try-again')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="dashboard-header">
                <h2>
                    <FontAwesomeIcon icon={faChartLine} />
                    {t('control-panel')} - {formatMonth(currentDate)}
                </h2>
                
                <div className="month-navigation">
                    <button 
                        className="btn secondary"
                        onClick={() => navigateMonth(-1)}
                        title={t('previous-month')}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    
                    <span className="current-month">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {formatMonth(currentDate)}
                    </span>
                    
                    <button 
                        className="btn secondary"
                        onClick={() => navigateMonth(1)}
                        title={t('next-month')}
                        disabled={currentDate.getMonth() >= new Date().getMonth() && currentDate.getFullYear() >= new Date().getFullYear()}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>

            {/* Monthly Statistics Cards */}
            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faPlane} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{monthlyStats.totalFlights}</div>
                        <div className="stat-label">{t('flights-completed')}</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{monthlyStats.uniquePilots}</div>
                        <div className="stat-label">{t('active-pilots')}</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{formatHours(monthlyStats.totalHours)}</div>
                        <div className="stat-label">{t('flying-hours')}</div>
                    </div>
                </div>
            </div>

            {/* Pilot Statistics Table */}
            {monthlyStats.pilotStats.length > 0 && (
                <div className="section">
                    <h3>
                        <FontAwesomeIcon icon={faUsers} />
                        {t('pilot-statistics')} - {formatMonth(currentDate)}
                    </h3>
                    <div className="pilots-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('pilot')}</th>
                                    <th>{t('callsign')}</th>
                                    <th>{t('flights')}</th>
                                    <th>{t('hours')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyStats.pilotStats.map((pilot, index) => (
                                    <tr key={pilot.pilot.id}>
                                        <td>
                                            <div className="pilot-name">
                                                {index < 3 && <span className="rank-badge">#{index + 1}</span>}
                                                {pilot.pilot.firstName} {pilot.pilot.lastName}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="callsign">{pilot.pilot.callsign}</span>
                                        </td>
                                        <td>
                                            <span className="flights-count">{pilot.flights}</span>
                                        </td>
                                        <td>
                                            <span className="hours-count">{formatHours(pilot.hours)}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Inactive This Month */}
            {inactiveCurrent.pilots.length > 0 && (
                <div className="section warning">
                    <h3>
                        <FontAwesomeIcon icon={faUserClock} />
                        {t('flew-previous-not-current', { 
                            previousMonth: inactiveCurrent.previousMonth, 
                            previousYear: inactiveCurrent.previousYear,
                            currentMonth: inactiveCurrent.currentMonth,
                            currentYear: inactiveCurrent.currentYear,
                            count: inactiveCurrent.count
                        })}
                    </h3>
                    <div className="inactive-pilots">
                        {inactiveCurrent.pilots.map(pilot => (
                            <div key={pilot.id} className="pilot-card inactive">
                                <div className="pilot-info">
                                    <span className="pilot-name">{pilot.firstName} {pilot.lastName}</span>
                                    <span className="pilot-callsign">{pilot.callsign}</span>
                                </div>
                                <div className="pilot-stats">
                                    <span className="last-month-flights">{t('flights-last-month', { count: pilot.lastMonthFlights })}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Long Term Inactive */}
            {longInactive.pilots.length > 0 && (
                <div className="section danger">
                    <h3>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {t('inactive-pilots-long', { count: longInactive.count })}
                    </h3>
                    <div className="inactive-pilots">
                        {longInactive.pilots.map(pilot => (
                            <div key={pilot.id} className="pilot-card long-inactive">
                                <div className="pilot-info">
                                    <span className="pilot-name">{pilot.firstName} {pilot.lastName}</span>
                                    <span className="pilot-callsign">{pilot.callsign}</span>
                                </div>
                                <div className="pilot-stats">
                                    <span className="last-flight">
                                        {t('last-flight')}: {formatDaysAgo(pilot.daysSinceLastFlight)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* No data states */}
            {monthlyStats.totalFlights === 0 && (
                <div className="no-data">
                    <FontAwesomeIcon icon={faPlane} size="3x" />
                    <h3>{t('no-flights-registered', { month: formatMonth(currentDate) })}</h3>
                    <p>{t('flights-appear-after-validation')}</p>
                </div>
            )}
        </div>
    );
}